import { Datepicker } from '@acpaas-ui/react-components';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { fetchReset } from '../actions/fetch.actions';
import { resourceUpdate } from '../actions/resources.actions';
import AttachmentsList from '../components/attachments/AttachmentsList';
import { INPUT_STATES } from '../components/forms/forms.utils';
import TextArea from '../components/forms/TextArea';
import TextField from '../components/forms/TextField';
import { MODALS } from '../constants/modals.constants';
import dateValidators from '../form-validators/date.validators';
import { useForm } from '../hooks/useForm.hook';
import { useModal } from '../hooks/useModal.hook';
import { MEASURE_MOMENTS_RESOURCE } from '../resources/measure.moments.resource';
import procedureService from '../services/api/procedure.service';
import AttachmentDetailsModal from './AttachmentDetailsModal';
import AttachmentUploadModal from './AttachmentUploadModal';
import Modal from './Modal';
import ModalTitle from './ModalTitle';

const MeasureMomentCreateModal = ({
  dossierId,
  goalId,
  addingIndicatorId,
  isOpen,
  onClose,
  onDossierRefetch,
  onAttachmentsRefetch
}) => {
  const dispatch = useDispatch();
  const uploadModal = useModal(MODALS.ATTACHMENT_UPLOAD);
  const detailsModal = useModal(MODALS.ATTACHMENT_DETAILS);
  const attachmentDetailForm = useForm(MODALS.ATTACHMENT_DETAILS.initialState);
  const { data, input, load, reset, isSubmitting, error, submitStart, submitOk, submitError } = useForm(
    MODALS.MEASURE_MOMENT_CREATE.initialState
  );
  const [dateError, setDateError] = useState('');

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    reset();
    load(MODALS.MEASURE_MOMENT_CREATE.initialState);
    setDateError('');
    onClose && onClose();
  };

  const handleConfirm = () => {
    const dateValidation = dateValidators.dateTextField(data.date);
    if (dateValidation) {
      setDateError(dateValidation);
      return;
    }
    submitStart();
    procedureService.addIndicatorMeasureMoment(dossierId, goalId, addingIndicatorId, data, {
      onError: (err) => submitError(err),
      onSuccess: (resp) => {
        submitOk();
        handleClose();
        onDossierRefetch();
        onAttachmentsRefetch();
        dispatch(
          resourceUpdate(
            { key: `${MEASURE_MOMENTS_RESOURCE.key}_${addingIndicatorId}` },
            'data',
            resp?.dossier?.procedure?.goals
              ?.find((g) => g.id === goalId)
              ?.indicators?.find((i) => i.id === addingIndicatorId)?.measureMoments ?? []
          )
        );
        dispatch(fetchReset({ key: `${MEASURE_MOMENTS_RESOURCE.key}_${addingIndicatorId}` }));
      }
    });
  };

  const handleDateChange = (value) => {
    setDateError('');
    input(value, 'date');
  };

  const handleAttachmentAdd = (attachment) => {
    input([...data.attachments, { ...attachment, id: uuid() }], 'attachments');
    uploadModal.onClose();
  };

  const handleAttachmentDelete = () => {
    input(
      data.attachments.filter((f) => f.id !== attachmentDetailForm?.data?.id),
      'attachments'
    );
    detailsModal.onClose();
  };

  return (
    <Modal
      className="spo-o-measure-moment-create-modal"
      title={<ModalTitle hasError={!!error} title="Meetmoment toevoegen" />}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      canConfirm={!dateError}
      isSubmitting={isSubmitting}
      onConfirmLabel="Toevoegen"
      onConfirmIcon="ai-check-1"
    >
      <div className="row u-margin-bottom">
        <div className="col-xs-12 col-md-6">
          <Datepicker
            required
            format={'DD/MM/YYYY'}
            mask={'99/99/9999'}
            label="Datum"
            activeDate={data.date}
            state={dateError ? 'error' : ''}
            description={dateError ? <span className="u-text-danger">{dateError}</span> : ''}
            onChange={handleDateChange}
            disabled={isSubmitting}
          />
        </div>
      </div>
      <div className="row u-margin-bottom">
        <div className="col-xs-12">
          <TextArea
            className="spo-a-textarea spo-a-textarea--small"
            label="Vaststelling"
            value={data.determination}
            state={INPUT_STATES.DEFAULT}
            onChange={(value) => input(value, 'determination')}
            disabled={isSubmitting}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-lg">
        <div className="col-xs-12">
          <TextField
            label="Methode"
            value={data.method}
            state={INPUT_STATES.DEFAULT}
            onChange={(value) => input(value, 'method')}
            disabled={isSubmitting}
          />
        </div>
      </div>
      <div className="u-margin-bottom">
        <AttachmentsList
          title="Bijlagen"
          attachments={data.attachments?.map((att) => ({ ...att, fileName: att?.attachment?.name }))}
          onOpen={(attachment) => {
            attachmentDetailForm.load(attachment);
            detailsModal.onOpen();
          }}
          onUpload={() => {
            uploadModal.onOpen();
          }}
        />
      </div>
      {error && <small className="u-text-danger">{error}</small>}
      <AttachmentUploadModal {...uploadModal} onConfirm={handleAttachmentAdd} inline />
      <AttachmentDetailsModal
        {...detailsModal}
        form={attachmentDetailForm}
        dossierId={dossierId}
        onDelete={handleAttachmentDelete}
      />
    </Modal>
  );
};

export default MeasureMomentCreateModal;
